import {skipToken} from '@reduxjs/toolkit/query';

import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {UseFormReturn, useWatch} from 'react-hook-form';

import {isNil, isNotNil} from 'ramda';

import {useOnMount, useQueryState} from 'shared/web';

import {useSelectedFilters} from '../../../../../hooks/SelectedFiltersContext';
import {useCatalogue} from '../../../../../hooks/useCatalogue';
import {usePriceReport} from '../../../../../hooks/usePriceReport';
import {useTrimLevelsCatalog} from '../../../../../hooks/useTrimLevelsCatalog';
import {useGetRelativeFiltersQuery} from '../../../../../store/omneticApi';
import {RelativeFilterConfigBody} from '../../../../../types/api/api';
import {FilterFormData} from '../../../../../types/FilterFormData';
import {PriceReportType} from '../../../../../types/PriceReportType';
import {QueryStateKeys} from '../../../../../utils/routing';
import {applyPresetToForm} from '../utils/applyPresetToForm';
import {findMatchingPreset} from '../utils/findMatchingPreset';
import {isPresetControlledFormField} from '../utils/presetControlledFormFields';

type UseRelativeFilterPresetsControllerOptions = {
  enabled: boolean;
  priceReport: PriceReportType;
  formApi: UseFormReturn<FilterFormData>;
  onFilterChange: () => void;
};

export function useRelativeFilterPresetsController(
  options: UseRelativeFilterPresetsControllerOptions
) {
  const {enabled, priceReport, formApi, onFilterChange} = options;
  const {requestData} = usePriceReport();
  const customerId = requestData?.customerId;

  const firstRegistrationOnYearGte = useWatch({
    control: formApi.control,
    name: 'firstRegistrationOnYearGte',
  });
  const firstRegistrationOnYearLte = useWatch({
    control: formApi.control,
    name: 'firstRegistrationOnYearLte',
  });
  const {catalog: trimLevelsCatalog} = useTrimLevelsCatalog(
    priceReport,
    firstRegistrationOnYearGte,
    firstRegistrationOnYearLte
  );
  const {hasFeatureTranslation, isLoading: isCatalogueLoading} = useCatalogue();

  const {
    data: presets = [],
    isLoading,
    isError,
  } = useGetRelativeFiltersQuery(enabled && isNotNil(customerId) ? {customerId} : skipToken);

  const [activePresetId, setActivePresetId] = useState<string | null>(null);
  // RHF drops setValue calls issued during the very first commit (the form is not
  // "mounted" yet) — with warm RTK Query caches the bootstrap effect would run
  // exactly then. Flipping this state defers the bootstrap by one render.
  const [isFormMounted, setIsFormMounted] = useState(false);
  useOnMount(() => setIsFormMounted(true));
  const isApplyingPresetRef = useRef(false);
  const hasCompletedInitialPresetSyncRef = useRef(false);
  // The ?presetId deep link is one-shot per mount; the URL param may survive the
  // clear (filter → URL sync works with a params snapshot), so a ref guards re-application.
  const hasConsumedDeepLinkRef = useRef(false);
  const syncActivePresetFromFormRef = useRef<() => void>(() => undefined);

  // Memoised to keep bootstrap / form-watch effect deps stable, not for render performance.
  const applyPreset = useCallback(
    (config: RelativeFilterConfigBody) => {
      if (!enabled || isCatalogueLoading) {
        return;
      }

      isApplyingPresetRef.current = true;
      try {
        applyPresetToForm(
          config,
          priceReport,
          formApi,
          onFilterChange,
          trimLevelsCatalog,
          hasFeatureTranslation
        );
      } finally {
        isApplyingPresetRef.current = false;
      }
    },
    [
      enabled,
      priceReport,
      formApi,
      onFilterChange,
      trimLevelsCatalog,
      hasFeatureTranslation,
      isCatalogueLoading,
    ]
  );

  // Memoised to keep preset-matching callback deps stable, not for render performance.
  const matchPresetParams = useMemo(
    () => ({
      priceReport,
      trimLevelsCatalog,
      isKnownFeature: hasFeatureTranslation,
    }),
    [priceReport, trimLevelsCatalog, hasFeatureTranslation]
  );

  const {hasAnyFilter} = useSelectedFilters();
  // Deep link (e.g. from the Overview saved-filters card): ?presetId=… selects
  // the given preset on arrival instead of the default one.
  const [lingeringDeepLinkParam, , clearDeepLinkedPresetId] = useQueryState(
    QueryStateKeys.FilterPreset,
    true
  );
  // The first-render value, frozen — other mount effects (filter → URL sync) may
  // rewrite the search string before the bootstrap effect gets to run, so the live
  // router value cannot be trusted there.
  const [deepLinkedPresetId] = useState(lingeringDeepLinkParam);

  // useEffect: drop the consumed ?presetId when the filter → URL sync resurrects it
  // from a stale params snapshot. Not derived state or key remount (writes to the
  // URL), not an event handler (the resurrection happens in a debounced subscription
  // elsewhere), not RTK Query (no server involved), not useOnMount (the param
  // reappears only later); useQueryState exposes no change callback to subscribe to.
  useEffect(
    function clearConsumedDeepLinkParam() {
      if (hasConsumedDeepLinkRef.current && isNotNil(lingeringDeepLinkParam)) {
        clearDeepLinkedPresetId();
      }
    },
    [lingeringDeepLinkParam, clearDeepLinkedPresetId]
  );
  // Memoised to keep bootstrap / sync effect deps stable, not for render performance.
  const systemPreset = useMemo(
    () => (enabled ? (presets.find((preset) => preset.system) ?? null) : null),
    [enabled, presets]
  );
  // Memoised to keep bootstrap / sync effect deps stable, not for render performance.
  const defaultPreset = useMemo(
    () => (enabled ? (presets.find((preset) => preset.default) ?? null) : null),
    [enabled, presets]
  );

  // Memoised to keep RTK-query / form-watch effect deps stable, not for render performance.
  const syncActivePresetFromForm = useCallback(() => {
    if (!enabled || isApplyingPresetRef.current || isLoading || isCatalogueLoading) {
      return;
    }

    if (!hasCompletedInitialPresetSyncRef.current && !hasAnyFilter && isNotNil(defaultPreset)) {
      return;
    }

    const formSnapshot = formApi.getValues();
    const matchedPresetId =
      findMatchingPreset(presets, formSnapshot, matchPresetParams)?.id ?? null;

    setActivePresetId((current) => (current === matchedPresetId ? current : matchedPresetId));
  }, [
    enabled,
    isLoading,
    isCatalogueLoading,
    hasAnyFilter,
    defaultPreset,
    presets,
    formApi,
    matchPresetParams,
  ]);
  syncActivePresetFromFormRef.current = syncActivePresetFromForm;

  // Memoised to keep bootstrap effect deps stable, not for render performance.
  const applyDefaultPresetIfAvailable = useCallback((): boolean => {
    if (!enabled || isLoading || isCatalogueLoading || isNil(defaultPreset)) {
      return false;
    }

    setActivePresetId(defaultPreset.id);
    applyPreset(defaultPreset.config);
    return true;
  }, [enabled, isLoading, isCatalogueLoading, defaultPreset, applyPreset]);

  // useEffect: subscribe to RHF field changes outside event handlers. Not derived state (active
  // preset is UI state), not useOnMount (no cleanup support for formApi.watch), not key remount
  // (would discard unrelated filter state). syncActivePresetFromForm is read via ref so the
  // subscription is not recreated on every presets/catalogue update.
  useEffect(
    function syncActivePresetOnPresetControlledFormChange() {
      if (!enabled) {
        return;
      }

      const subscription = formApi.watch((_, {name}) => {
        if (isApplyingPresetRef.current) {
          return;
        }
        if (isNotNil(name) && !isPresetControlledFormField(name)) {
          return;
        }
        syncActivePresetFromFormRef.current();
      });
      return () => subscription.unsubscribe();
    },
    [enabled, formApi]
  );

  // useEffect: apply the default preset once presets and catalogue finish loading. Not derived
  // state (writes to RHF), not an event handler (runs on async RTK Query readiness), not key
  // remount (would discard unrelated filter state), not useOnMount (data is not ready on mount).
  useEffect(
    function applyDefaultPresetOnInitialLoad() {
      if (!enabled || !isFormMounted || hasCompletedInitialPresetSyncRef.current) {
        return;
      }

      if (isLoading || isCatalogueLoading) {
        return;
      }

      // A deep-linked preset is an explicit user choice — it wins over the default
      // preset and over any filters remembered from a previous visit.
      if (!hasConsumedDeepLinkRef.current && isNotNil(deepLinkedPresetId)) {
        hasConsumedDeepLinkRef.current = true;
        const linkedPreset = presets.find((preset) => preset.id === deepLinkedPresetId);
        clearDeepLinkedPresetId();
        if (isNotNil(linkedPreset)) {
          hasCompletedInitialPresetSyncRef.current = true;
          setActivePresetId(linkedPreset.id);
          applyPreset(linkedPreset.config);
          return;
        }
      }

      if (hasAnyFilter) {
        return;
      }

      if (formApi.formState.isDirty) {
        hasCompletedInitialPresetSyncRef.current = true;
        return;
      }

      hasCompletedInitialPresetSyncRef.current = true;

      if (isNotNil(defaultPreset)) {
        applyDefaultPresetIfAvailable();
      }
    },
    [
      enabled,
      isFormMounted,
      isLoading,
      isCatalogueLoading,
      hasAnyFilter,
      defaultPreset,
      applyDefaultPresetIfAvailable,
      formApi.formState.isDirty,
      deepLinkedPresetId,
      clearDeepLinkedPresetId,
      presets,
      applyPreset,
    ]
  );

  // useEffect: re-sync active preset when presets or catalogue data arrive. Equivalent to reacting
  // to RTK Query data — not derived state (activePresetId is separate UI state).
  useEffect(
    function syncActivePresetWhenPresetsOrCatalogReady() {
      syncActivePresetFromForm();
    },
    [syncActivePresetFromForm]
  );

  const displayedPresetId =
    activePresetId ?? (!hasAnyFilter ? (defaultPreset?.id ?? systemPreset?.id ?? null) : null);

  const isDefaultPresetBootstrapPending =
    enabled && isNotNil(defaultPreset) && (isLoading || isCatalogueLoading);

  return {
    customerId,
    presets,
    isLoading,
    isError,
    isCatalogueLoading,
    activePresetId,
    setActivePresetId,
    displayedPresetId,
    defaultPreset,
    hasDefaultPreset: isNotNil(defaultPreset),
    isDefaultPresetBootstrapPending,
    applyPreset,
    applyDefaultPresetIfAvailable,
  };
}

export type RelativeFilterPresetsController = ReturnType<typeof useRelativeFilterPresetsController>;
